/**
 * @fileoverview BankBalanceDisplay component shows bank balance and difference indicator.
 * Displays total bank balance with difference from budget ending balance.
 */

import styles from './BankBalanceDisplay.module.css';
import MoneyDisplay from '../common/MoneyDisplay';

/**
 * BankBalanceDisplay Component
 *
 * Displays bank balance information on the right side of month navigation.
 * Shows total bank balance and difference indicator (match/mismatch with budget).
 * On mobile, difference is hidden by default and shown when isExpanded is true.
 *
 * Props:
 * - bankBalance (number): Total bank account balance
 * - endingBalance (number): Budget ending balance for comparison
 * - onUpdateBankBalance (function): Called when user clicks to update bank balance
 * - isExpanded (boolean): Whether details are expanded (syncs with budget balance on mobile)
 */
function BankBalanceDisplay({ bankBalance, endingBalance, onUpdateBankBalance, isExpanded }) {
    // Calculate difference between bank balance and budget ending balance
    const difference = bankBalance != null && endingBalance != null
        ? bankBalance - endingBalance
        : 0;
    const isMatch = Math.abs(difference) < 0.005; // Match if difference rounds to $0.00
    const budgetIsHigher = difference < 0;

    return (
        <div className={styles.container}>
            {/* Bank Balance - Clickable when handler provided */}
            <div
                className={styles.bankBalance}
                onClick={onUpdateBankBalance}
                role={onUpdateBankBalance ? "button" : undefined}
                tabIndex={onUpdateBankBalance ? 0 : undefined}
                onKeyPress={onUpdateBankBalance ? (e) => e.key === 'Enter' && onUpdateBankBalance() : undefined}
                style={{ cursor: onUpdateBankBalance ? 'pointer' : 'default' }}
            >
                <span>Bank: <MoneyDisplay value={bankBalance} /></span>
            </div>

            {/* Difference Indicator - Always visible on desktop, collapsible on mobile */}
            <div className={`${styles.bankDifference} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                <div className={`${styles.differenceItem} ${isMatch ? styles.match : styles.mismatch}`}>
                    {isMatch ? (
                        <span className={styles.matchIcon}>✓</span>
                    ) : (
                        <span className={styles.arrow}>
                            {budgetIsHigher ? '↑' : '↓'}
                        </span>
                    )}
                    <span className={styles.differenceAmount}>
                        {isMatch ? 'Match' : <MoneyDisplay value={Math.abs(difference)} />}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default BankBalanceDisplay;
